<script>
import { AbcHeading } from "@ordbogen-npm/abc-dsk";
import { computed, defineComponent, onMounted, onUnmounted, reactive, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";

import TgButton from "@/abc-dsk/components/Button/TgButton.vue";
import { getErrorMessage } from "@/common/api/utils";
import VocabService from "@/common/api/vocab.service";
import NotificationService from "@/common/notification.service";
import { ClassQuizStateEnum } from "@/common/types/vocab";
import { flushVueQueue } from "@/common/utils";
import ClassQuizEnded from "@/components/vocabularyTrainer/ClassQuizEnded.vue";
import ClassQuizLobby from "@/components/vocabularyTrainer/ClassQuizLobby.vue";
import ClassQuizQuestion from "@/components/vocabularyTrainer/ClassQuizQuestion.vue";
import ClassQuizRanking from "@/components/vocabularyTrainer/ClassQuizRanking.vue";
import ClassQuizStats from "@/components/vocabularyTrainer/ClassQuizStats.vue";

export default defineComponent({
  components: { ClassQuizLobby, ClassQuizQuestion, TgButton, AbcHeading },
  setup() {
    // Import globals
    const router = useRouter();
    const route = useRoute();
    const { t } = useI18n();

    // Quiz state
    /** @type {import("@/common/types/vocab").ClassQuiz} */
    const classQuiz = reactive({
      code: route.params.quizCode,
      state: null,
      players: [],
      topPlayers: [],
      question: {},
      hasAdditionalQuestions: null,
      answer: {},
      userAnswer: null
    });

    const refreshClassQuiz = async () => {
      // Stop polling if quiz ended.
      if (classQuiz.state === ClassQuizStateEnum.Ended) {
        clearInterval(pollInterval.value);
        return;
      }

      try {
        const { data } = await VocabService.getClassQuiz(route.params.quizCode);
        Object.assign(classQuiz, { ...data.data });
      } catch (error) {
        // Quietly ignore errors
      }
    };

    const pollInterval = ref(null);
    onMounted(async () => {
      await refreshClassQuiz();

      if (route.name === "vocab.classQuiz.stats") {
        return showStats();
      }

      pollInterval.value = setInterval(() => refreshClassQuiz(), 1000);
    });
    onUnmounted(() => clearInterval(pollInterval.value));

    // Template variables
    const contentComponentMap = {
      [ClassQuizStateEnum.Lobby]: ClassQuizLobby,
      [ClassQuizStateEnum.Prompting]: ClassQuizQuestion,
      [ClassQuizStateEnum.Answers]: ClassQuizQuestion,
      [ClassQuizStateEnum.Ranking]: ClassQuizRanking,
      [ClassQuizStateEnum.Ended]: ClassQuizEnded,
      [ClassQuizStateEnum.Stats]: ClassQuizStats
    };
    const contentComponent = computed(() => contentComponentMap[classQuiz.state]);
    const questionId = computed(() => classQuiz.question?.id);
    const disableContinue = ref(false);

    const getRankings = async () => {
      try {
        const { data } = await VocabService.classQuizSetRankingState(route.params.quizCode);
        Object.assign(classQuiz, { ...data.data });
      } catch (error) {
        if (getErrorMessage(error) === "class_quiz_has_ended") {
          NotificationService.error(t("api.class_quiz_has_ended"));
        }
      }
    };
    const getNextQuestion = async () => {
      try {
        const { data } = await VocabService.classQuizSetPromptingState(route.params.quizCode);
        Object.assign(classQuiz, { ...data.data });
      } catch (error) {
        if (getErrorMessage(error) === "class_quiz_has_ended") {
          NotificationService.error(t("api.class_quiz_has_ended"));
        }
      }
    };

    const endQuiz = async () => {
      try {
        await VocabService.endClassQuiz(route.params.quizCode.toString());
      } catch (error) {
        // Gracefully catch error
      }

      router.push({ name: "vocab.classQuiz.stats", params: route.params });
    };

    const showStats = () => (classQuiz.state = ClassQuizStateEnum.Stats);

    // Listeners
    const onContinue = async () => {
      disableContinue.value = true;

      switch (classQuiz.state) {
        case ClassQuizStateEnum.Answers:
          if (classQuiz.hasAdditionalQuestions !== false) {
            await getRankings();
          } else {
            // Even if there is no more questions
            // setting the quiz to prompting state will trigger another poll,
            // which correctly sets the quiz state.
            await getNextQuestion();
          }
          break;
        case ClassQuizStateEnum.Ended:
          endQuiz();
          showStats();
          break;
        default:
          await getNextQuestion();
      }

      // Ensure state is up-to-date,
      // as the question component might flicker with the prev. question
      disableContinue.value = false;
      await flushVueQueue();
    };

    return {
      classQuiz,
      onStartQuiz: () => getNextQuestion(),
      onContinue,

      contentComponent,
      questionId,
      disableContinue
    };
  }
});
</script>

<template>
  <main class="d-flex flex-column">
    <template v-if="classQuiz.state && contentComponent">
      <Component
        :is="contentComponent"
        :key="questionId"
        :class-quiz="classQuiz"
        :disable-continue="disableContinue"
        @start-quiz="onStartQuiz()"
        @continue="onContinue()"
      />
    </template>
  </main>
</template>

<style lang="less" scoped>
@import "@/abc-dsk/theming/index.less";

main {
  background: @colors[@white];
}
</style>
